import { approvalBlocker } from "../utils/approvals";

// Shared furniture for changes waiting on an admin. The Approvals tab is the
// full list; these are the same requests shown where the change would land —
// in a group's card, its panel, and the member's card.

export function PendingBadge({ count, title = "Waiting for an admin to approve" }) {
  if (!count) return null;
  return (
    <span
      className="bg-amber-100 text-amber-800 border border-amber-300 rounded px-1 text-[10px] font-semibold leading-tight"
      title={title}
    >
      {count === true ? "pending" : `${count} pending`}
    </span>
  );
}

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
}

/**
 * One waiting request, with whatever the viewer is allowed to do about it.
 * Admins approve or reject; the person who asked can withdraw their own.
 */
export function PendingRequestRow({ request, currentUser, players, groups, onApprove, onReject, onCancel, busy }) {
  const isAdmin = currentUser?.role === "admin";
  const isMine = request.requestedById === currentUser?.id;
  const blocker = approvalBlocker(request, players, groups);

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-gray-800 font-medium">{request.summary || request.type}</p>
          <p className="text-gray-500 mt-0.5">
            {request.requestedBy || "someone"}
            {isMine && " (you)"} · {when(request.requestedAt)}
          </p>
          {blocker && (
            <p className="text-red-700 mt-1">
              Can't be applied: {blocker}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {isAdmin && (
            <>
              <button
                disabled={busy || Boolean(blocker)}
                onClick={() => onApprove?.(request)}
                title={blocker || "Approve and apply this change"}
                className="bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white rounded px-2 py-1 font-semibold transition-colors"
              >
                Approve
              </button>
              <button
                disabled={busy}
                onClick={() => onReject?.(request)}
                title="Reject this change"
                className="border border-gray-300 hover:bg-white disabled:opacity-40 text-gray-700 rounded px-2 py-1 font-semibold transition-colors"
              >
                Reject
              </button>
            </>
          )}
          {!isAdmin && isMine && (
            <button
              disabled={busy}
              onClick={() => onCancel?.(request)}
              title="Withdraw this request"
              className="border border-gray-300 hover:bg-white disabled:opacity-40 text-gray-600 rounded px-2 py-1 transition-colors"
            >
              Withdraw
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function PendingRequestList({ requests = [], label = "Waiting for approval", ...rest }) {
  if (requests.length === 0) return null;
  return (
    <div>
      <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1.5">
        {label}
      </div>
      <div className="space-y-1.5">
        {requests.map(r => (
          <PendingRequestRow key={r.id} request={r} {...rest} />
        ))}
      </div>
    </div>
  );
}
